/**
 * User Agreements Section
 * Read-only panel inside UserEditModal showing the user's agreement responses
 * with an expand-row to reveal the per-agreement response history.
 */

import { useState } from 'react';
import {
  useUserAgreementHistory,
  useUserAgreements,
} from '../../hooks/useAgreementApi';
import type { UserProfile } from '../../lib/types/user';
import type { UserAgreementResponse } from '../../lib/types/agreement';
import { LoadingSpinner } from '../ui/LoadingSpinner';

interface Props {
  user: UserProfile;
}

function StatusBadge({ status }: { status: UserAgreementResponse['status'] }) {
  const palette: Record<UserAgreementResponse['status'], string> = {
    agreed:
      'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200',
    disagreed: 'bg-rose-100 text-rose-800 dark:bg-rose-900 dark:text-rose-200',
    not_answered:
      'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
  };
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${palette[status]}`}
    >
      {status.replace('_', ' ')}
    </span>
  );
}

function HistoryRow({ uid, slug }: { uid: string; slug: string }) {
  const { data, isLoading, error } = useUserAgreementHistory(uid, slug, 50);
  if (isLoading) {
    return (
      <div className="p-3 flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
        <LoadingSpinner className="w-4 h-4" />
        <span>Loading history…</span>
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="p-3 text-sm text-rose-600 dark:text-rose-400">
        Failed to load history.
      </div>
    );
  }
  if (data.entries.length === 0) {
    return (
      <div className="p-3 text-sm text-gray-500 dark:text-gray-400">
        No history entries.
      </div>
    );
  }
  return (
    <ul className="divide-y divide-gray-200 dark:divide-gray-700 text-xs">
      {data.entries.map(entry => (
        <li key={entry.id} className="flex items-center justify-between p-2">
          <div>
            <StatusBadge status={entry.response} />
            <span className="ml-2 text-gray-500 dark:text-gray-400">
              v{entry.agreedVersion}
            </span>
            <span className="ml-2 text-gray-400 dark:text-gray-500">
              · {entry.source}
            </span>
          </div>
          <time className="text-gray-500 dark:text-gray-400">
            {new Date(entry.respondedAt).toLocaleString()} local
          </time>
        </li>
      ))}
    </ul>
  );
}

export function UserAgreementsSection({ user }: Props) {
  const { data, isLoading, error } = useUserAgreements(user.uid);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  return (
    <div className="pt-6 border-t border-gray-200 dark:border-gray-700">
      <div className="mb-3">
        <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">
          Agreements
        </h4>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          User responses to recorded agreements (read-only).
        </p>
      </div>

      {isLoading && (
        <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
          <LoadingSpinner className="w-4 h-4" />
          <span>Loading…</span>
        </div>
      )}
      {error && (
        <p className="text-sm text-rose-600 dark:text-rose-400">
          Failed to load agreement responses.
        </p>
      )}
      {data && data.responses.length === 0 && (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          This user has no agreement responses on record.
        </p>
      )}
      {data && data.responses.length > 0 && (
        <ul className="divide-y divide-gray-200 dark:divide-gray-700 rounded-md border border-gray-200 dark:border-gray-700">
          {data.responses.map(resp => {
            const isOpen = !!expanded[resp.slug];
            return (
              <li key={resp.slug}>
                <button
                  type="button"
                  onClick={() =>
                    setExpanded(prev => ({
                      ...prev,
                      [resp.slug]: !prev[resp.slug],
                    }))
                  }
                  className="w-full flex items-center justify-between p-3 text-left hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100 font-mono">
                      {resp.slug}
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      <StatusBadge status={resp.status} />
                      {resp.agreedVersion !== null && (
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          v{resp.agreedVersion}
                        </span>
                      )}
                      {resp.respondedAt && (
                        <span className="text-xs text-gray-400 dark:text-gray-500">
                          {new Date(resp.respondedAt).toLocaleString()} local
                        </span>
                      )}
                    </div>
                  </div>
                  <span className="text-gray-400 dark:text-gray-500 text-xs">
                    {isOpen ? 'Hide history' : 'Show history'}
                  </span>
                </button>
                {isOpen && (
                  <div className="bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700">
                    <HistoryRow uid={user.uid} slug={resp.slug} />
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
